import type { AppMetaContextValue, VariablesContextValue } from '@core/context';
import type { ReactNode } from 'react';

import { useContext, useEffect } from 'react';

import { AppMetaContext, VariablesContext } from '@core/context';
import { useProjectStore, useSuperHubStore } from '@core/store';
import getMetaDescription from '@core/utils/getMetaDescription';

interface ConnectMetadataProps {
  children: ReactNode;
}

/**
 * Update the App's metadata to reflect the current page when navigating client-side to
 * Docs, Reference, Changelog, or Custom Page routes.
 */
export default function ConnectMetadata({ children }: ConnectMetadataProps) {
  const { updateAppMeta, ...AppMeta } = useContext(AppMetaContext) as AppMetaContextValue;
  const variables = useContext(VariablesContext) as VariablesContextValue;
  const [navLinks, projectDescription] = useProjectStore(s => [s.data.appearance.navigation.links, s.data.description]);
  const [routeAction, routeSection, slug, isReady, isLoading, document] = useSuperHubStore(s => [
    s.routeAction,
    s.routeSection,
    s.slug,
    s.document.isReady,
    s.document.isLoading,
    s.document.data,
  ]);

  useEffect(() => {
    if (!isReady || isLoading || !routeSection) return;
    if (!['docs', 'reference', 'changelog', 'page'].includes(routeSection)) return;

    if (routeAction === 'create') {
      const title = routeSection === 'changelog' ? 'New Post' : 'New Page';
      const nextMetaValue = {
        title,
        metaTitle: title,
        type: routeSection,
        description: projectDescription,
      };
      updateAppMeta({
        ...AppMeta,
        ...nextMetaValue,
      });
      return;
    }

    if (!slug) {
      const title =
        routeSection === 'docs'
          ? navLinks.guides.label
          : routeSection === 'page'
            ? 'Page'
            : navLinks[routeSection].label;

      const nextMetaValue = {
        title,
        metaTitle: title,
        description: projectDescription,
        type: routeSection,
      };
      updateAppMeta({
        ...AppMeta,
        ...nextMetaValue,
      });
      return;
    }

    if (!document) return;
    const description = getMetaDescription(document, variables);
    const nextMetaValue = {
      description: String(description) || projectDescription,
      hidden: document.privacy.view !== 'public',
      image: document.metadata?.image?.url ? [document.metadata.image.url] : [],
      keywords: document.metadata?.keywords || undefined,
      metaTitle: document.metadata?.title || document.title || '',
      robots: 'allow_crawlers' in document ? (document.allow_crawlers === 'enabled' ? 'index' : 'noindex') : undefined,
      slug: document.slug,
      title: document.title || '',
      type: routeSection,
    };
    updateAppMeta({
      ...AppMeta,
      ...nextMetaValue,
    });
  }, [
    AppMeta,
    document,
    isLoading,
    isReady,
    navLinks,
    projectDescription,
    routeAction,
    routeSection,
    slug,
    updateAppMeta,
    variables,
  ]);

  return <>{children}</>;
}
